import type { ReactNode } from 'react';
import { createContext, useContext, useMemo } from 'react';
import useEmployeeDisplayDataByAliases from './useEmployeeDisplayDataByAliases';
import { EmployeeDisplayData, EmployeeDisplayDataByAlias } from './types';

type EmployeeDisplayDataContextValue = {
  employeeDisplayDataByAlias: EmployeeDisplayDataByAlias;
  hasAliases: boolean;
};

const EmployeeDisplayDataContext = createContext<EmployeeDisplayDataContextValue | null>(null);

const EmployeeDisplayDataProvider = ({ aliases, children }: { aliases: string[]; children: ReactNode }) => {
  const employeeDisplayDataByAlias = useEmployeeDisplayDataByAliases(aliases);
  const contextValue = useMemo(
    () => ({
      employeeDisplayDataByAlias,
      hasAliases: aliases.length > 0,
    }),
    [employeeDisplayDataByAlias, aliases.length],
  );

  return <EmployeeDisplayDataContext.Provider value={contextValue}>{children}</EmployeeDisplayDataContext.Provider>;
};

const useEmployeeDisplayDataContext = () => useContext(EmployeeDisplayDataContext);

export const useEmployeeDisplayData = (alias?: string): EmployeeDisplayData | undefined => {
  const employeeDisplayDataContext = useEmployeeDisplayDataContext();

  return alias ? employeeDisplayDataContext?.employeeDisplayDataByAlias[alias] : undefined;
};

export default EmployeeDisplayDataProvider;
